import { InputAdornment } from '@mui/material';
import { Search as SearchIcon } from 'react-feather';
import { useTheme } from '@mui/material/styles';
import { CleanTextField } from 'components/styled/CleanTextField';
import type { TextFieldProps } from '@mui/material';

/**
 * SearchInput - Reusable search input component with icon
 *
 * Provides a consistent search input with:
 * - Search icon that doesn't interfere with input (pointer-events: none)
 * - Placeholder text that doesn't overlap with icon
 * - Theme-aware styling using CleanTextField
 * - All TextField props support
 *
 * Usage:
 * ```tsx
 * <SearchInput
 *   placeholder="Search by name, team ID, target service ID, UUID…"
 *   value={searchText}
 *   onChange={(e) => setSearchText(e.target.value)}
 *   fullWidth
 * />
 * ```
 *
 * With clear button:
 * ```tsx
 * <SearchInput
 *   placeholder="Search..."
 *   value={searchText}
 *   onChange={(e) => setSearchText(e.target.value)}
 *   InputProps={{
 *     endAdornment: searchText && (
 *       <InputAdornment position="end">
 *         <IconButton size="small" onClick={() => setSearchText("")}>
 *           <X size={16} />
 *         </IconButton>
 *       </InputAdornment>
 *     ),
 *   }}
 * />
 * ```
 *
 * @see src/modules/TeamModule/components/MembersPage.tsx - Members search
 * @see src/modules/TeamModule/components/TeamsV2Page.tsx - Teams search
 */
export const SearchInput: React.FC<TextFieldProps> = ({
  placeholder = 'Search...',
  InputProps,
  ...props
}) => {
  const theme = useTheme();

  return (
    <CleanTextField
      {...props}
      placeholder={placeholder}
      InputProps={{
        ...InputProps,
        startAdornment: (
          <InputAdornment
            position="start"
            sx={{
              pointerEvents: 'none', // Prevents icon from blocking input interactions
              color: theme.palette.text.secondary,
            }}
          >
            <SearchIcon size={20} />
          </InputAdornment>
        ),
      }}
    />
  );
};
